import { create } from 'zustand'
import { devtools } from 'zustand/middleware'
import { trpcClient } from '../../../utils/trpc'
import type { PecCycleSearchResult } from '../types'

/**
 * @fileoverview PEC Dashboard Store
 *
 * Zustand store for PEC cycles list page with search, pagination, and actions
 */

export interface PecDashboardStore {
	// State
	pecCycles: PecCycleSearchResult['data']
	isLoading: boolean
	error: string | null
	searchQuery: string
	selectedCycle: PecCycleSearchResult['data'][0] | null
	pagination: {
		page: number
		perPage: number
		totalCount: number
		totalPages: number
	}

	// Actions
	initialize: (organizationId: string) => Promise<void>
	search: (params: {
		organizationId: string
		search?: string
		status?: 'draft' | 'ready' | 'active' | 'closed' | 'archived'
		sortBy?: 'name' | 'createdAt' | 'updatedAt' | 'launchedAt'
		order?: 'asc' | 'desc'
		page?: number
		perPage?: number
	}) => Promise<void>
	setSearchQuery: (query: string) => void
	setSelectedCycle: (cycle: PecCycleSearchResult['data'][0] | null) => void
	deleteSelectedCycle: () => Promise<void>
	duplicateSelectedCycle: () => Promise<void>
	setPage: (page: number) => void
}

export const createPecDashboardStore = () => {
	return create<PecDashboardStore>()(
		devtools(
			(set, get) => ({
				// Initial state
				pecCycles: [],
				isLoading: false,
				error: null,
				searchQuery: '',
				selectedCycle: null,
				pagination: {
					page: 1,
					perPage: 20,
					totalCount: 0,
					totalPages: 0,
				},

				// Initialize - load initial data
				initialize: async (organizationId: string) => {
					set({ isLoading: true, error: null })

					try {
						const result = await trpcClient.client.pec.search.query({
							organizationId,
							page: 1,
							perPage: 20,
						})

						set({
							pecCycles: result.data,
							pagination: {
								page: result.meta.page,
								perPage: result.meta.perPage,
								totalCount: result.meta.total,
								totalPages: Math.ceil(result.meta.total / result.meta.perPage),
							},
							isLoading: false,
						})
					} catch (error) {
						console.error('Failed to initialize PEC dashboard:', error)
						set({
							error: 'Failed to load PEC cycles',
							isLoading: false,
						})
					}
				},

				// Search with filters
				search: async (params) => {
					set({ isLoading: true, error: null })

					try {
						const result = await trpcClient.client.pec.search.query({
							organizationId: params.organizationId,
							search: params.search || get().searchQuery,
							page: params.page || get().pagination.page,
							perPage: params.perPage || get().pagination.perPage,
							status: params.status,
							sortBy: params.sortBy,
							order: params.order,
						})

						set({
							pecCycles: result.data,
							pagination: {
								page: result.meta.page,
								perPage: result.meta.perPage,
								totalCount: result.meta.total,
								totalPages: Math.ceil(result.meta.total / result.meta.perPage),
							},
							isLoading: false,
						})
					} catch (error) {
						console.error('Failed to search PEC cycles:', error)
						set({
							error: 'Failed to search PEC cycles',
							isLoading: false,
						})
					}
				},

				// Set search query
				setSearchQuery: (query: string) => {
					set({ searchQuery: query })
				},

				// Set selected cycle
				setSelectedCycle: (cycle: PecCycleSearchResult['data'][0] | null) => {
					set({ selectedCycle: cycle })
				},

				// Delete selected cycle
				deleteSelectedCycle: async () => {
					const { selectedCycle } = get()
					if (!selectedCycle) return

					set({ isLoading: true, error: null })

					try {
						await trpcClient.client.pec.delete.mutate({
							id: selectedCycle.id,
							organizationId: selectedCycle.organizationId,
						})

						// Refresh list
						await get().search({ organizationId: selectedCycle.organizationId })

						set({ selectedCycle: null, isLoading: false })
					} catch (error) {
						console.error('Failed to delete PEC cycle:', error)
						set({
							error: 'Failed to delete PEC cycle',
							isLoading: false,
						})
						throw error
					}
				},

				// Duplicate selected cycle
				duplicateSelectedCycle: async () => {
					const { selectedCycle } = get()
					if (!selectedCycle) return

					set({ isLoading: true, error: null })

					try {
						await trpcClient.client.pec.create.mutate({
							organizationId: selectedCycle.organizationId,
							name: `${selectedCycle.name} (Copy)`,
							description: selectedCycle.description || undefined,
							populationId: selectedCycle.populationId || undefined,
							promptText: selectedCycle.promptText,
							ratingScale: selectedCycle.ratingScale,
							emailTemplateId: selectedCycle.emailTemplateId || undefined,
						})

						// Refresh list
						await get().search({ organizationId: selectedCycle.organizationId })

						set({ selectedCycle: null, isLoading: false })
					} catch (error) {
						console.error('Failed to duplicate PEC cycle:', error)
						set({
							error: 'Failed to duplicate PEC cycle',
							isLoading: false,
						})
						throw error
					}
				},

				// Set page
				setPage: (page: number) => {
					set((state) => ({
						pagination: { ...state.pagination, page },
					}))
				},
			}),
			{ name: 'pec-dashboard-store' },
		),
	)
}
